<!-- A page body in the current LCD font. Emoji graphemes render through
     <PixelEmoji>; everything else is plain text that inherits font and colour
     (non-Latin scripts fall back through the --font-dot stack, never `?`).

     `type-on` (ms per character) types the text onto the screen left to right —
     the LCD's arrival animation on text devices. The untyped remainder is laid
     out but invisible, so the line never reflows as it fills. Skipped under
     prefers-reduced-motion. -->
<template>
  <span class="pm-text" :class="{ 'is-typing': typing }">
    <template v-for="(seg, i) in segments" :key="i">
      <PixelEmoji v-if="seg.emoji" :char="seg.text" :class="{ 'is-pending': typing && i >= shown }" />
      <span v-else-if="typing" :class="{ 'is-pending': i >= shown }">{{ seg.text }}</span>
      <template v-else>{{ seg.text }}</template>
    </template>
  </span>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { graphemes, isEmoji } from '../pm/text.js';
import { prefersReducedMotion } from '../core/a11y.js';
import PixelEmoji from './PixelEmoji.vue';

const props = defineProps({
  body: { type: String, default: '' },
  /** Milliseconds per character to type the text on; 0 = show at once. */
  typeOn: { type: Number, default: 0 },
});
const emit = defineEmits(['typed']);

const typing = ref(false);
const shown = ref(0);
let timer = null;

const parts = computed(() => graphemes(props.body || ''));

// Typing needs one element per grapheme; at rest, consecutive text merges into
// runs so a screen of prose is a handful of text nodes rather than 160 spans.
const segments = computed(() => {
  const gs = parts.value;
  if (typing.value) return gs.map((g) => ({ text: g, emoji: isEmoji(g) }));
  const out = [];
  for (const g of gs) {
    const emoji = isEmoji(g);
    const last = out[out.length - 1];
    if (!emoji && last && !last.emoji) last.text += g;
    else out.push({ text: g, emoji });
  }
  return out;
});

function stop() {
  if (timer) clearInterval(timer);
  timer = null;
}

function start() {
  stop();
  const total = parts.value.length;
  if (!props.typeOn || total < 2 || prefersReducedMotion()) {
    typing.value = false;
    emit('typed');
    return;
  }
  typing.value = true;
  shown.value = 0;
  timer = setInterval(() => {
    shown.value += 1;
    if (shown.value >= total) {
      stop();
      typing.value = false;
      emit('typed');
    }
  }, props.typeOn);
}

watch(() => [props.body, props.typeOn], start, { immediate: true });
onBeforeUnmount(stop);
</script>
